/**
 * Margin.
 *
 * Two different numbers get called "margin" in a shop, and confusing them is how a product
 * ends up sold at a loss that nobody notices:
 *
 *   margen   (over PRICE)  (precio - costo) / precio   ← what the accounts mean
 *   recargo  (over COST)   (precio - costo) / costo    ← what the shopkeeper says
 *
 * Buy at C$16 and sell at C$20: that is a 20% margen and a 25% recargo. The configured floor
 * is a margen, and the UI labels both so nobody has to guess which one is on screen.
 */

import { mulDivCeil, type Centavos } from './money.js';
import { costOf, type Milli, type UnitCost } from './quantity.js';

/** Gross margin over price, in basis points. 2500 = 25%. */
export function margenBp(precio: Centavos, costo: Centavos): number {
  if (precio <= 0) return 0;
  return Math.round(((precio - costo) / precio) * 10_000);
}

/** Markup over cost, in basis points. */
export function recargoBp(precio: Centavos, costo: Centavos): number {
  if (costo <= 0) return 0;
  return Math.round(((precio - costo) / costo) * 10_000);
}

/** What one SALE unit costs, given the product's average cost per base unit. */
export function costoDeUnidadVenta(costoBase: UnitCost, factor: Milli): Centavos {
  return costOf(costoBase, factor);
}

/**
 * The price that hits a target margin over price.
 *
 *   precio = costo / (1 - margen)
 *
 * Used by the bulk-entry screen to price several hundred products at once without anyone
 * doing arithmetic on a phone at the counter.
 */
export function precioParaMargen(costo: Centavos, margenObjetivoBp: number): Centavos {
  const restante = 10_000 - margenObjetivoBp;
  if (restante <= 0) return costo;
  return mulDivCeil(costo, 10_000, restante);
}

/**
 * The margin the app offers when it suggests a price out of nothing.
 *
 * It lives here rather than in a screen because two screens suggest prices — the bulk pricing
 * list and a single product's detail — and they were each carrying their own 3_000, one of
 * them as a bare literal beside a hardcoded "30%" in a tooltip. Same number, same meaning, no
 * way to change one without finding the other.
 */
export const MARGEN_SUGERIDO_BP = 3_000;

/** The price that hits a target markup over cost. */
export function precioParaRecargo(costo: Centavos, recargoObjetivoBp: number): Centavos {
  return mulDivCeil(costo, 10_000 + recargoObjetivoBp, 10_000);
}

export function formatBp(bp: number): string {
  return `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 1)}%`;
}

export type NivelMargen = 'perdida' | 'bajo' | 'normal';

/** Classifies a margin for display. Loss is its own category, not merely "low". */
export function nivelMargen(margen: number, minimoBp: number): NivelMargen {
  if (margen <= 0) return 'perdida';
  if (margen < minimoBp) return 'bajo';
  return 'normal';
}
